import { useMutation, useQuery } from '@tanstack/react-query';
import { Printer, ReceiptText } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { get } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { money } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { DataTable, StatTile } from '@/components/ui/layout';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ShellEmpty, ShellSectionHeading } from '@/components/clinical-shell/ShellPanel';
import { PackageLineBadge } from '../../../billing/PackageLineBadge';
import { admissionIsOpen, type Admission, type RunningBill } from '../types';

export function runningBillKey(admissionId: string) {
  return ['admission-bill', admissionId] as const;
}

export function BillSection({ admission, hasStayPackage }: { admission: Admission; hasStayPackage: boolean }) {
  const toast = useToast();
  const current = admissionIsOpen(admission);
  const bill = useQuery({
    queryKey: runningBillKey(admission.id),
    queryFn: () => get<RunningBill>(`/ipd/admissions/${admission.id}/bill`),
  });
  const print = useMutation({
    mutationFn: () => get<{ html: string }>(`/ipd/admissions/${admission.id}/bill/print`),
    onSuccess: (doc) => printDocument(doc.html),
    onError: (error) => toast.error(tr('ipd', 'could_not_print'), describeError(error)),
  });
  const totals = bill.data?.totals ?? admission.account;

  return (
    <>
      <ShellSectionHeading
        icon={ReceiptText}
        title={tr('ipd', 'running_bill')}
        description={tr('ipd', 'ws_bill_desc')}
        actions={
          <Button size="sm" variant="outline" onClick={() => print.mutate()} loading={print.isPending}>
            <Printer className="h-4 w-4" aria-hidden /> {current ? tr('ipd', 'ws_print_interim_bill') : tr('ipd', 'ws_print_final_bill')}
          </Button>
        }
      />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label={tr('ipd', 'billed')} value={money(totals.billedMinor)} />
        <StatTile label={tr('ipd', 'ws_paid')} value={money(totals.paidMinor)} />
        <StatTile label={tr('ipd', 'advances')} value={money(totals.depositedMinor)} />
        <StatTile
          label={totals.balanceMinor < 0 ? tr('ipd', 'owing') : tr('ipd', 'credit')}
          value={money(Math.abs(totals.balanceMinor))}
          tone={totals.balanceMinor < 0 ? 'destructive' : 'success'}
        />
      </div>
      {bill.isLoading && <Spinner />}
      {bill.isError && <Alert variant="destructive">{describeError(bill.error)}</Alert>}
      {bill.data && bill.data.groups.length === 0 && <ShellEmpty>{tr('ipd', 'ws_nothing_billed_yet')}</ShellEmpty>}
      {bill.data?.groups.map((group) => (
        <Card key={group.context}>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-sm">{group.context.toUpperCase()}</CardTitle>
            <span className="tabular font-medium">{money(group.subtotalMinor)}</span>
          </CardHeader>
          <CardContent>
            <DataTable
              dense
              rows={group.items.map((item, index) => ({ ...item, line: index }))}
              rowKey={(row) => `${group.context}-${row.line}`}
              columns={[
                {
                  header: tr('ipd', 'description'),
                  cell: (row) => (
                    <span className="inline-flex flex-wrap items-center gap-2">
                      {row.description}
                      <PackageLineBadge role={row.packageRole ?? null} billHasPackage={hasStayPackage} />
                    </span>
                  ),
                },
                { header: tr('ipd', 'qty'), align: 'right', cell: (row) => row.qty },
                { header: tr('ipd', 'amount'), align: 'right', cell: (row) => money(row.netMinor) },
              ]}
            />
          </CardContent>
        </Card>
      ))}
    </>
  );
}
